// Lead capture for the contact form, the "Request a Proposal" popup, and the
// newsletter box. Submissions go to the Adroit backend (/api/v1/forms/*) first;
// if the backend is unconfigured or unreachable, we fall back to Formspree so a
// lead is never lost. Keeps a single place for payload mapping + error handling.
import { apiPost, isApiConfigured } from '../api/client';
import { getAttribution } from './attribution';
import { trackLead } from './gtag';

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/mrgnagnj';

async function submitToFormspree(payload) {
    const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
        },
        body: JSON.stringify(payload),
    });

    let data = {};
    try {
        data = await res.json();
    } catch (e) {
        // Non-JSON response — fall through to the status check below.
    }

    if (!res.ok) {
        // Formspree returns { errors: [{ message }] }; older shape used { error }.
        const message = Array.isArray(data.errors)
            ? data.errors.map((err) => err.message).join(', ')
            : data.error || 'Something went wrong. Please try again.';
        throw new Error(message);
    }

    return data;
}

// Map either form shape (Form.js uses First_Name/Last_Name; Popup.js uses Name)
// onto the backend contact schema.
function toContactPayload(form) {
    const name = form.Name || `${form.First_Name || ''} ${form.Last_Name || ''}`.trim();
    return {
        name,
        email: form.Email,
        phone: form.Phone || undefined,
        budget: form.Budget || undefined,
        service: form.Services || undefined,
        message: form.Message,
        // Which ad/campaign brought them here (captured on their first page view).
        campaign: getAttribution(),
    };
}

// Submit a contact/proposal lead. Returns a friendly success message.
export async function submitContact(form) {
    const fireConversion = () =>
        trackLead({ service: form.Services, budget: form.Budget, formType: 'contact' });

    if (isApiConfigured()) {
        try {
            const res = await apiPost('/forms/contact', toContactPayload(form));
            fireConversion();
            return res.message || "Thanks! We'll be in touch shortly.";
        } catch (err) {
            // Backend rejected the payload with a validation error — surface it
            // instead of silently double-sending to Formspree.
            if (err.status === 400) throw new Error(err.message);
            // Otherwise (network/5xx/unconfigured) fall back to Formspree.
        }
    }
    await submitToFormspree(form);
    fireConversion();
    return "Thanks! We'll be in touch shortly.";
}

// Subscribe an email to the newsletter list.
export async function submitNewsletter(email) {
    if (isApiConfigured()) {
        try {
            const res = await apiPost('/forms/newsletter', { email, campaign: getAttribution() });
            trackLead({ formType: 'newsletter' });
            return res.message || 'Subscribed — thank you!';
        } catch (err) {
            if (err.status === 400) throw new Error(err.message);
        }
    }
    await submitToFormspree({ Email: email, type: 'newsletter', _subject: 'Newsletter subscription' });
    trackLead({ formType: 'newsletter' });
    return 'Subscribed — thank you!';
}
